// ReportNoSeedHandheld.tsx
import { useState, useMemo } from 'react';
import {
    Box,
    Stack,
    Typography,
    Button,
    Divider,
    FormControl,
    FormHelperText,
    MenuItem,
    Select,
    Chip,
    Collapse,
} from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';

import { useCommonStyles } from '../../styles/GenericStyle';
import ScanInput from '../ScanInput';
import ScanButton from '../spLot/ScanIcon';
import { useReportNoSeedForMaterials, useValidateScannedMaterialForSplot } from '../../api/vegApl';
import { useSnackbar } from '../../contexts/SnackbarContext';
import { useGlobalState } from '../../contexts/GlobalState';
import useGetSortedRefCrops from '../../api/useGetSortedRefCrops';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import FullScreenActionLoader from '../common/FullScreenActionLoader';
import { DASH_PLACEHOLDER } from '../../utils/missingValue';

// ──────────────────────────────────────────────
// Types (mirrored from reportnoseedpopup.tsx)
// ──────────────────────────────────────────────
interface HarvestInstance {
    material_harvest_id: number;
    splot_id: string | null;
    required_grams: string;
    registered_grams: string;
    harvest_status: 'Registered' | 'Processing' | 'No Seed' | null;
    is_current_harvest: boolean;
}

interface MaterialDetail {
    matid: string;
    trial_id: string;
    splot_id: string;
    crop_code: string;
    site_code: string;
    material_id: number;
    total_registered_grams: string;
    total_required_grams: string;
    harvests: HarvestInstance[];
}

interface ReportNoSeedPayload {
    matid: string;
    remark_id: number;
    another_harvest_required: boolean;
    user_id: number;
}

export type ReportNoSeedHandheldProps = {
    onBack: () => void;
    onDone: () => void;
};

// ──────────────────────────────────────────────
// Constants (same as webpage)
// ──────────────────────────────────────────────

export default function ReportNoSeedHandheld({ onBack, onDone }: ReportNoSeedHandheldProps) {
    const classes = useCommonStyles();
    const { showSnackbar } = useSnackbar();
    const { reportNoSeedRemarks, state: globalState } = useGlobalState();
    const { data: crops = [] } = useGetSortedRefCrops();

    // Step 1: scan material, Step 2: confirm + select remark
    const [step, setStep] = useState<1 | 2>(1);
    const [materialIdInput, setMaterialIdInput] = useState('');
    const [selectedMaterial, setSelectedMaterial] = useState<MaterialDetail | null>(null);
    const [selectedRemark, setSelectedRemark] = useState('');
    const [remarkError, setRemarkError] = useState('');
    const [isValidating, setIsValidating] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isMaterialDetailsExpanded, setIsMaterialDetailsExpanded] = useState(false);

    // ── Derived state ─────────────────────────
    const selectedHarvest = useMemo(
        () =>
            selectedMaterial?.harvests?.find((h) => {
                const status = String(h?.harvest_status ?? '').trim().toLowerCase();
                return status !== 'registered' && status !== 'no seed';
            }) ?? null,
        [selectedMaterial],
    );

    const hasEligibleHarvest = Boolean(selectedHarvest);

    const cropName = useMemo(() => {
        if (!selectedMaterial?.crop_code) return DASH_PLACEHOLDER;
        const crop = crops.find((c: any) => c.crop_xrd_oid === selectedMaterial.crop_code);
        return crop?.name ?? selectedMaterial.crop_code;
    }, [selectedMaterial?.crop_code, crops]);

    const remarkOptions = useMemo(() => {
        const opts = (reportNoSeedRemarks ?? [])
            .filter((r) => r.remark_name && r.remark_id != null)
            .map((r) => ({ label: r.remark_name as string, value: String(r.remark_id) }));
        opts.sort((a, b) => a.label.localeCompare(b.label));
        return opts;
    }, [reportNoSeedRemarks]);

    const canSubmit = hasEligibleHarvest && Boolean(selectedRemark) && !isSubmitting;
    const isActionInProgress = isValidating || isSubmitting;

    // ── Handlers ──────────────────────────────
    const handleReset = () => {
        setStep(1);
        setMaterialIdInput('');
        setSelectedMaterial(null);
        setSelectedRemark('');
        setRemarkError('');
        setIsValidating(false);
        setIsSubmitting(false);
    };

    const handleBack = () => {
        if (step === 2) {
            // Go back to scan step
            handleReset();
        } else {
            onBack();
        }
    };

    // ── API: validate material ─────────────────
    const { mutate: validateScannedMaterial } = useValidateScannedMaterialForSplot({
        mutation: {
            onSuccess: (response: any) => {
                const rawData = response?.data?.data ?? response?.data;

                const apiErrorMessage =
                    response?.data?.error ||
                    response?.data?.detail ||
                    (typeof rawData === 'object' ? rawData?.error || rawData?.detail : '');

                if (apiErrorMessage) {
                    setIsValidating(false);
                    showSnackbar({ message: apiErrorMessage, severity: 'error' });
                    return;
                }

                const materialFromApi =
                    rawData &&
                    typeof rawData === 'object' &&
                    typeof rawData?.matid === 'string' &&
                    Array.isArray(rawData?.harvests)
                        ? (rawData as MaterialDetail)
                        : null;

                if (!materialFromApi) {
                    setIsValidating(false);
                    showSnackbar({ message: 'Enter a valid MatID', severity: 'error' });
                    return;
                }

                setIsValidating(false);
                setSelectedMaterial(materialFromApi);
                setStep(2);
            },
            onError: (error: any) => {
                setIsValidating(false);
                const errorMessage =
                    error?.response?.data?.detail || error?.message || 'Failed to validate scanned material';
                showSnackbar({ message: errorMessage, severity: 'error' });
            },
        },
    });

    // ── API: report no seed ────────────────────
    const { mutate: reportNoSeedForMaterial } = useReportNoSeedForMaterials({
        mutation: {
            onSuccess: (response: any) => {
                setIsSubmitting(false);
                const apiMessage =
                    response?.data?.message || response?.data?.detail || 'No Seed reported successfully';
                showSnackbar({ message: apiMessage, severity: 'success' });
                onDone();
            },
            onError: (error: any) => {
                setIsSubmitting(false);
                const errorMessage =
                    error?.response?.data?.detail || error?.message || 'Failed to report no seed';
                showSnackbar({ message: errorMessage, severity: 'error' });
            },
        },
    });

    const handleScanValidate = () => {
        const normalizedId = materialIdInput.trim().toUpperCase();
        if (!normalizedId) {
            showSnackbar({ message: 'Please enter or scan Material ID', severity: 'warning' });
            return;
        }
        setIsValidating(true);
        validateScannedMaterial({ data: { matid: normalizedId } });
    };

    const handleSubmit = () => {
        setRemarkError('');

        if (!hasEligibleHarvest) {
            showSnackbar({
                message: 'No eligible harvest available to report as No Seed',
                severity: 'warning',
            });
            return;
        }

        if (!selectedRemark) {
            setRemarkError('Please select a remark');
            return;
        }

        if (!globalState?.user_id_db) {
            showSnackbar({ message: 'Unable to identify current user', severity: 'error' });
            return;
        }

        if (!selectedMaterial || !selectedHarvest) return;

        const payload: ReportNoSeedPayload = {
            matid: selectedMaterial.matid,
            remark_id: Number(selectedRemark),
            another_harvest_required: true,
            user_id: Number(globalState.user_id_db),
        };

        setIsSubmitting(true);
        reportNoSeedForMaterial({ data: payload });
    };

    // ── Detail row helper ──────────────────────
    const detailsRow = (label: string, value: string | number) => (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
            }}
        >
            <Typography variant="caption" color="text.secondary" className={classes.labelNoto12Bold400_hh}>
                {label}
            </Typography>
            <Typography variant="body2" className={classes.labelNoto14Bold700_hh} sx={value === DASH_PLACEHOLDER ? { fontSize: 16, fontWeight: 600 } : undefined}>
                {value}
            </Typography>
        </Box>
    );

    // ── Render ───────────────────────────────
    return (
        <>
            <FullScreenActionLoader open={isActionInProgress} />
            <Box width="100%" display="flex" flexDirection="column" alignItems="center" sx={{ minHeight: 'calc(100vh - 140px)' }}>
            <Divider sx={{ width: '100%' }} />

            {/* Header */}
            <Box display="flex" width="100%" alignItems="center" justifyContent="flex-start">
                <ArrowBackIosIcon
                    sx={{ verticalAlign: 'middle', cursor: 'pointer', fontSize: 28 }}
                    onClick={handleBack}
                />
                <Box sx={{ py: 1.25, px: 1.5, flex: 1, minWidth: 0 }}>
                    <Typography component="div" className={classes.labelNoto20Bold500_hh}>
                        Report No Seed
                        {step === 2 && selectedMaterial && (
                            <Typography component="span" sx={{ display: 'block', fontSize: '18.2px', fontWeight: 400, color: 'text.secondary', mt: 0.25 }} className={classes.labelNoto14Bold400_hh}>
                                {selectedMaterial.matid}
                            </Typography>
                        )}
                    </Typography>
                    {step === 1 && (
                        <Typography
                            component="div"
                            className={classes.labelNoto12Bold400_hh}
                            sx={{ color: 'text.secondary', mt: 0.25 }}
                        >
                            Enter or Scan Material ID
                        </Typography>
                    )}
                </Box>
            </Box>

            <Divider sx={{ borderColor: 'grey.400', borderBottomWidth: '1px', width: '100%' }} />

            {/* ── STEP 1: Scan Material ID ── */}
            {step === 1 && (
                <Box sx={{ width: '100%', pt: '16px', px: 2, pb: 2, display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <Box sx={{ width: '100%', mt: '8px' }}>
                        <ScanButton />
                    </Box>
                    <Stack direction="row" spacing={1} sx={{ width: '100%', mt: '24px', alignItems: 'flex-end' }}>
                        <ScanInput
                            softFocusOnMount
                            submitOnScan={false}
                            scanPickupEnabled={false}
                            defaultValue={materialIdInput}
                            onChange={(val: string) => setMaterialIdInput(val)}
                            onScan={(val: string) => setMaterialIdInput(val)}
                            onEnterPress={handleScanValidate}
                            manualEdit
                            error={false}
                            label={
                                <>
                                    Material ID <Box component="span" sx={{ color: '#DC2626' }}>*</Box>
                                </>
                            }
                            placeholder="Scan or Enter Material ID"
                            errorText=""
                        />
                        {materialIdInput.trim() && (
                        <Button
                            variant="contained"
                            className={classes.blueButton_hh}
                            disabled={isValidating}
                            sx={{
                                height: 40,
                                width: 40,
                                minWidth: 40,
                                '& .MuiButton-startIcon, & .MuiButton-endIcon': { margin: 0, padding: 0 },
                            }}
                            startIcon={<ArrowForwardIcon sx={{ color: 'white', fontSize: 26 }} />}
                            onClick={handleScanValidate}
                        />
                        )}
                    </Stack>
                </Box>
            )}

            {/* ── STEP 2: Material details + remark ── */}
            {step === 2 && selectedMaterial && (
                <Box
                    sx={{
                        width: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                        minHeight: 0,
                    }}
                >
                    {/* Scrollable content area */}
                    <Box
                        sx={{
                            pt: '16px',
                            px: 2,
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 2,
                            overflowY: 'auto',
                            flex: 1,
                            pb: '100px',
                        }}
                    >
                    {/* Warning banner if no eligible harvest */}
                    {!hasEligibleHarvest && (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                p: 1.5,
                                borderRadius: '8px',
                                bgcolor: '#FFF7ED',
                                border: '1px solid #FED7AA',
                            }}
                        >
                            <WarningAmberOutlinedIcon color="warning" sx={{ fontSize: 24 }} />
                            <Typography variant="body2" color="text.secondary" className={classes.labelNoto12Bold400_hh}>
                                No eligible harvest found to report as No Seed.
                            </Typography>
                        </Box>
                    )}

                    {/* Material Details card - Collapsible */}
                    <Box
                        sx={{
                            borderRadius: '10px',
                            border: '1px solid #E5E7EB',
                            bgcolor: '#F5F5F5',
                            overflow: 'hidden',
                            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                    >
                        <Box
                            onClick={() => setIsMaterialDetailsExpanded(!isMaterialDetailsExpanded)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                pt: '8px',
                                pr: '10px',
                                pb: '8px',
                                pl: '10px',
                                cursor: 'pointer',
                                transition: 'background-color 0.2s ease',
                                '&:hover': {
                                    bgcolor: '#EFEFEF',
                                },
                            }}
                        >
                            <Typography variant="subtitle2" className={classes.labelNoto14Bold700_hh}>
                                Material Details
                            </Typography>
                            <ArrowDropDownIcon
                                sx={{
                                    fontSize: 24,
                                    transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    transform: isMaterialDetailsExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                                }}
                            />
                        </Box>
                        <Collapse
                            in={isMaterialDetailsExpanded}
                            timeout="auto"
                            sx={{
                                '& .MuiCollapse-wrapperInner': {
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    pt: '4px',
                                    pr: '10px',
                                    pb: '8px',
                                    pl: '10px',
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(2, 1fr)',
                                    rowGap: '4px',
                                    columnGap: '16px',
                                }}
                            >
                                {detailsRow('Crop', cropName)}
                                {detailsRow('Trial ID', selectedMaterial.trial_id ?? DASH_PLACEHOLDER)}
                                {detailsRow('Registered Grams', selectedMaterial.total_registered_grams ?? DASH_PLACEHOLDER)}
                                {detailsRow('Required Grams', selectedMaterial.total_required_grams ?? DASH_PLACEHOLDER)}
                            </Box>
                        </Collapse>
                    </Box>

                    {/* Harvest Information card */}
                    <Box
                        sx={{
                            borderRadius: '10px',
                            pt: '17px',
                            pr: '17px',
                            pb: '12px',
                            pl: '17px',
                            bgcolor: '#EFF6FF',
                            border: '1px solid #BEDBFF',
                        }}
                    >
                        <Typography variant="subtitle2" className={classes.labelNoto14Bold700_hh} sx={{ mb: 1.5 }}>
                            Harvest Information
                        </Typography>
                        <Stack spacing={1.25}>
                            {(selectedMaterial.harvests ?? []).map((harvest, index) => {
                                const isTarget =
                                    harvest.material_harvest_id === selectedHarvest?.material_harvest_id;
                                const harvestStatus = String(harvest.harvest_status ?? '').trim().toLowerCase();
                                const isGreenStatus =
                                    harvestStatus === 'registered' || harvestStatus === 'no seed';

                                return (
                                    <Box
                                        key={harvest.material_harvest_id}
                                        sx={{
                                            borderRadius: '4px',
                                            border: isGreenStatus ? '1px solid #BBF7D0' : '1px solid #FF8904',
                                            boxShadow: isGreenStatus ? 'none' : '0px 1px 2px -1px #0000001A',
                                            bgcolor: isGreenStatus ? '#F0FDF4' : '#FFF7ED',
                                            px: '12px',
                                            py: '10px',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                flexWrap: 'wrap',
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    minWidth: 0,
                                                    flex: '1 1 auto',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 0.5,
                                                    flexWrap: 'wrap',
                                                }}
                                            >
                                                <Typography
                                                    variant="body2"
                                                    className={classes.labelNoto14Bold600_hh}
                                                >
                                                    {`Harvest #${index + 1}`}
                                                </Typography>
                                                {isTarget && (
                                                    <Box
                                                        component="span"
                                                        className={classes.labelNoto12Bold500_hh}
                                                        sx={{
                                                            display: { xs: 'none', sm: 'inline-flex' },
                                                            alignItems: 'center',
                                                            justifyContent: 'flex-start',
                                                            px: 1,
                                                            height: 20,
                                                            borderRadius: '4px',
                                                            bgcolor: '#585858',
                                                            color: '#FFFFFF',
                                                            fontSize: '15.6px',
                                                            fontWeight: 500,
                                                            lineHeight: 1,
                                                            whiteSpace: 'nowrap',
                                                            textAlign: 'left',
                                                            flexShrink: 0,
                                                        }}
                                                    >
                                                        To be marked No Seed
                                                    </Box>
                                                )}
                                            </Box>
                                            <Chip
                                                label={harvest.harvest_status ?? 'Pending'}
                                                size="small"
                                                className={classes.labelNoto12Bold500_hh}
                                                sx={{
                                                    minWidth: 72,
                                                    height: 24,
                                                    borderRadius: '4px',
                                                    bgcolor: isGreenStatus ? '#DCFCE7' : '#FEF3C6',
                                                    color: isGreenStatus ? '#15803D' : '#D97706',
                                                    ml: 'auto',
                                                    flexShrink: 0,
                                                    '& .MuiChip-label': { px: 1 },
                                                }}
                                            />
                                        </Box>
                                        {isTarget && (
                                            <Box
                                                component="span"
                                                className={classes.labelNoto12Bold500_hh}
                                                sx={{
                                                    display: { xs: 'inline-flex', sm: 'none' },
                                                    mt: 0.75,
                                                    alignItems: 'center',
                                                    justifyContent: 'flex-start',
                                                    px: 1,
                                                    height: 20,
                                                    borderRadius: '4px',
                                                    bgcolor: '#585858',
                                                    color: '#FFFFFF',
                                                    fontSize: '15.6px',
                                                    fontWeight: 500,
                                                    lineHeight: 1,
                                                    whiteSpace: 'nowrap',
                                                    textAlign: 'left',
                                                }}
                                            >
                                                To be marked No Seed
                                            </Box>
                                        )}
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                            className={classes.labelNoto12Bold400_hh}
                                            sx={{ display: 'block', mt: 0.75 }}
                                        >
                                            <Box component="span" sx={{ display: 'block' }}>
                                                {harvest.splot_id ?? selectedMaterial.splot_id ?? 'N/A'}
                                            </Box>
                                            <Box component="span" sx={{ display: 'block' }}>
                                                {`${harvest.registered_grams}g registered`}
                                            </Box>
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Stack>
                        <Box
                            className={classes.labelNoto14Bold400_hh}
                            sx={{ mt: 1.25, display: 'flex', justifyContent: 'space-between' }}
                        >
                            <Typography variant="body2" color="text.secondary">
                                Total Harvests:
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {selectedMaterial.harvests?.length ?? 0}
                            </Typography>
                        </Box>
                    </Box>

                    {/* Remark selector */}
                    <FormControl fullWidth size="small" error={!!remarkError}>
                        <Typography
                            variant="body2"
                            className={classes.labelNoto14Bold400_hh}
                            sx={{ mb: '6px', color: '#4B5563' }}
                        >
                            Select Remarks <Box component="span" sx={{ color: '#DC2626' }}>*</Box>
                        </Typography>
                        <Select
                            IconComponent={KeyboardArrowDownOutlinedIcon}
                            displayEmpty
                            value={selectedRemark}
                            onChange={(e) => {
                                setSelectedRemark(e.target.value);
                                if (remarkError) setRemarkError('');
                            }}
                            sx={{ height: 40, '& .MuiSelect-icon': { fontSize: 28.8 } }}
                            renderValue={(selected) => {
                                const found = remarkOptions.find((o) => o.value === selected);
                                return found ? (
                                    <span>{found.label}</span>
                                ) : (
                                    <span className={classes.labelNoto14Bold400_hh} style={{ color: '#9CA3AF' }}>Select a Remark</span>
                                );
                            }}
                        >
                            {remarkOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                    {option.label}
                                </MenuItem>
                            ))}
                        </Select>
                        {remarkError && <FormHelperText className={classes.labelNoto14Bold400_hh}>{remarkError}</FormHelperText>}
                    </FormControl>
                    </Box>

                    {/* Sticky button at bottom */}
                    <Button
                        variant="contained"
                        disableRipple
                        className={classes.labelNoto16Bold400_hh}
                        disabled={!canSubmit}
                        loading={isSubmitting}
                        loadingPosition="start"
                        sx={{
                            position: 'fixed',
                            bottom: 20,
                            left: 16,
                            right: 16,
                            height: 48,
                            gap: '6px',
                            opacity: 1,
                            borderRadius: '12px',
                            backgroundColor: '#008F35',
                            boxShadow: '0px 4px 16px rgba(0, 143, 53, 0.4)',
                            color: !canSubmit ? '#9CA3AF' : '#FFFFFF',
                            textTransform: 'none',
                            zIndex: 1000,
                            '&:hover': {
                                backgroundColor: '#006B2B',
                                boxShadow: '0px 6px 20px rgba(0, 143, 53, 0.5)',
                            },
                            '&:disabled': {
                                backgroundColor: '#bdbdbd',
                                color: '#fff',
                            },
                        }}
                        onClick={handleSubmit}
                    >
                        {isSubmitting ? 'Submitting...' : 'Report No Seed'}
                    </Button>
                </Box>
            )}
            </Box>
        </>
    );
}
